import { z } from "zod";
import type { DateScopeOverrideInput } from "./date-scope.ts";
import {
  type CustomerJourneyScope,
  createCustomerJourneyScopeFromTracifyContext,
} from "./scope.ts";
import {
  getTracifyApiJson,
  type TracifyApiBasePath,
} from "./tracify-api-client.ts";
import type { TracifyContext } from "../ai/request-context.ts";

export const customerJourneyBreakdownDimensionSchema = z.enum([
  "channel",
  "campaign",
]);

export const customerJourneyCustomerTypeSchema = z.enum([
  "new",
  "returning",
  "total",
]);

export const customerJourneyPathSortBySchema = z.enum([
  "customer_revenue",
  "customer_purchase",
  "average_order_value",
  "number_of_customers",
  "number_of_touchpoints",
  "average_seconds_to_conversion",
]);

export const customerJourneySortOrderSchema = z.enum(["asc", "desc"]);

export type CustomerJourneyBreakdownDimension = z.infer<
  typeof customerJourneyBreakdownDimensionSchema
>;
export type CustomerJourneyBreakdown = CustomerJourneyBreakdownDimension;
export type CustomerJourneyCustomerType = z.infer<
  typeof customerJourneyCustomerTypeSchema
>;
export type CustomerJourneyPathSortBy = z.infer<
  typeof customerJourneyPathSortBySchema
>;
export type CustomerJourneySortOrder = z.infer<
  typeof customerJourneySortOrderSchema
>;

export type CustomerJourneyStats = {
  mean: number;
  p25: number;
  p50: number;
  p75: number;
  p95: number;
  distribution: Record<string, number>;
};

export type CustomerJourneySegments = {
  new: CustomerJourneyStats;
  returning: CustomerJourneyStats;
  total: CustomerJourneyStats;
};

export type CustomerJourneyOverviewResponse = {
  touchpoints_to_purchase: CustomerJourneySegments;
  time_to_conversion: CustomerJourneySegments;
};

export type CustomerJourneyCustomerMetrics = {
  new: number;
  returning: number;
  total: number;
};

export type CustomerJourneyChannelData = Record<
  string,
  CustomerJourneyCustomerMetrics
>;

export type CustomerJourneyRoleBreakdownResponse = {
  initiator: CustomerJourneyChannelData;
  holder: CustomerJourneyChannelData;
  closer: CustomerJourneyChannelData;
};

export type CustomerJourneyMetric = {
  customer_revenue: number;
  customer_purchase: number;
  average_order_value: number;
  number_of_customers: number;
  number_of_touchpoints: number;
  average_seconds_to_conversion: number;
  average_seconds_to_next: number[];
};

export type CustomerJourneyData = {
  new: CustomerJourneyMetric;
  returning: CustomerJourneyMetric;
  total: CustomerJourneyMetric;
};

export type CustomerJourneyBreakdownInfo = {
  path: string[];
};

export type CustomerJourneyBreakdownItem = {
  info: CustomerJourneyBreakdownInfo;
  new: CustomerJourneyMetric;
  returning: CustomerJourneyMetric;
  total: CustomerJourneyMetric;
  compared?: Omit<CustomerJourneyBreakdownItem, "compared">;
};

export type CustomerJourneyPagination = {
  offset: string | number;
  limit: number;
  total: number;
  has_next_page: boolean;
  detail?: string;
};

export type CustomerJourneyBreakdownResponse = {
  breakdown_dimension: CustomerJourneyBreakdownDimension;
  overall: CustomerJourneyData;
  overall_compared?: CustomerJourneyData;
  breakdown: {
    data: CustomerJourneyBreakdownItem[];
    pagination: CustomerJourneyPagination;
  };
};

export type CustomerJourneyFilterCondition = {
  name: string;
  operator: string;
  value: unknown;
  index?: number;
};

export type CustomerJourneyFilterClause = {
  logic: "and" | "or";
  conditions: CustomerJourneyFilterCondition[];
};

export type CustomerJourneyFilterExpression = {
  logic: "and" | "or";
  clauses: CustomerJourneyFilterClause[];
};

export type CustomerJourneyFiltersInput =
  | string
  | CustomerJourneyFilterCondition[]
  | CustomerJourneyFilterExpression
  | Record<string, unknown>;

export type CustomerJourneyTouchpoint = {
  journey_touchpoint_id: string;
  site_id: string;
  when: string;
  conversion_event_id: string;
  conversion_when: string;
  touchpoint_index: number;
  number_of_touchpoints: number;
  dimensions: Record<string, string>;
  seconds_to_next: number;
  seconds_to_conversion: number;
};

export type CustomerJourneyOrderTouchpointsResponse = {
  items: CustomerJourneyTouchpoint[];
  count: number;
};

export type CustomerJourneyDateFetchInput = {
  startDate?: string | null;
  endDate?: string | null;
};

export type CustomerJourneyRoleBreakdownFetchInput =
  CustomerJourneyDateFetchInput & {
    customerType?: CustomerJourneyCustomerType | null;
  };

export type CustomerJourneyPathsFetchInput = CustomerJourneyDateFetchInput & {
  breakdownDimension?: CustomerJourneyBreakdownDimension | null;
  customerType?: CustomerJourneyCustomerType | null;
  filters?: CustomerJourneyFiltersInput | null;
  limit?: number | null;
  offset?: number | null;
  sortBy?: CustomerJourneyPathSortBy | CustomerJourneyPathSortBy[] | null;
  sortOrder?: CustomerJourneySortOrder | null;
};

export type CustomerJourneyOrderTouchpointsFetchInput = {
  siteId: string;
  orderId: string;
  limit?: number | null;
  offset?: number | null;
};

type CustomerJourneyFetchOptions<Input> = {
  baseUrl: string;
  scope: CustomerJourneyScope;
  input: Input;
  token: string;
  timeoutMs?: number;
  maxRetries?: number;
  retryDelayMs?: number;
  requestId?: string;
  apiBasePath?: TracifyApiBasePath;
};

type CustomerJourneyFetchWithContextOptions<Input> = Omit<
  CustomerJourneyFetchOptions<Input>,
  "scope" | "input"
> & {
  context: TracifyContext;
  input: Input & DateScopeOverrideInput;
};

export type FetchCustomerJourneyOverviewOptions =
  CustomerJourneyFetchOptions<CustomerJourneyDateFetchInput>;
export type FetchCustomerJourneyRoleBreakdownOptions =
  CustomerJourneyFetchOptions<CustomerJourneyRoleBreakdownFetchInput>;
export type FetchCustomerJourneyPathsOptions =
  CustomerJourneyFetchOptions<CustomerJourneyPathsFetchInput>;
export type FetchCustomerJourneyOrderTouchpointsOptions = Omit<
  CustomerJourneyFetchOptions<CustomerJourneyOrderTouchpointsFetchInput>,
  "scope"
>;

export const DEFAULT_CUSTOMER_JOURNEY_PATH_BREAKDOWN_DIMENSION: CustomerJourneyBreakdownDimension =
  "channel";
export const DEFAULT_CUSTOMER_JOURNEY_PATH_LIMIT = 10;
export const MAX_CUSTOMER_JOURNEY_PATH_LIMIT = 25;
export const DEFAULT_CUSTOMER_JOURNEY_PATH_SORT_BY: CustomerJourneyPathSortBy =
  "customer_purchase";
export const DEFAULT_CUSTOMER_JOURNEY_PATH_SORT_ORDER: CustomerJourneySortOrder =
  "desc";
export const DEFAULT_CUSTOMER_JOURNEY_ORDER_TOUCHPOINTS_LIMIT = 100;
export const MAX_CUSTOMER_JOURNEY_ORDER_TOUCHPOINTS_LIMIT = 2_000;

function buildCustomerJourneyBaseSearchParams(
  scope: CustomerJourneyScope,
  input: CustomerJourneyDateFetchInput
) {
  const searchParams = new URLSearchParams();

  searchParams.append("start_date", input.startDate ?? scope.startDate);
  searchParams.append("end_date", input.endDate ?? scope.endDate);

  for (const csid of scope.storeIds) {
    searchParams.append("csids", csid);
  }

  return searchParams;
}

function appendCustomerTarget(
  searchParams: URLSearchParams,
  customerType: CustomerJourneyCustomerType | null | undefined,
  options: { includeTotal: boolean }
) {
  if (!customerType) {
    return;
  }

  if (customerType === "total" && !options.includeTotal) {
    return;
  }

  searchParams.append("filter_and_sort_target", customerType);
}

function normalizePathLimit(limit: number | null | undefined) {
  if (limit === null || limit === undefined || Number.isNaN(limit)) {
    return DEFAULT_CUSTOMER_JOURNEY_PATH_LIMIT;
  }

  return Math.min(
    Math.max(Math.trunc(limit), 1),
    MAX_CUSTOMER_JOURNEY_PATH_LIMIT
  );
}

function normalizeOrderTouchpointsLimit(limit: number | null | undefined) {
  if (limit === null || limit === undefined || Number.isNaN(limit)) {
    return DEFAULT_CUSTOMER_JOURNEY_ORDER_TOUCHPOINTS_LIMIT;
  }

  return Math.min(
    Math.max(Math.trunc(limit), 1),
    MAX_CUSTOMER_JOURNEY_ORDER_TOUCHPOINTS_LIMIT
  );
}

function normalizeOffset(offset: number | null | undefined) {
  if (offset === null || offset === undefined || Number.isNaN(offset)) {
    return null;
  }

  return Math.max(Math.trunc(offset), 0);
}

function normalizeFilters(
  filters:
    | CustomerJourneyFiltersInput
    | CustomerJourneyScope["filters"]
    | null
    | undefined
) {
  if (filters === null || filters === undefined) {
    return null;
  }

  if (typeof filters === "string") {
    const trimmed = filters.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (Array.isArray(filters) && filters.length === 0) {
    return null;
  }

  try {
    return JSON.stringify(filters);
  } catch {
    return null;
  }
}

function normalizeSortBy(
  sortBy:
    | CustomerJourneyPathSortBy
    | CustomerJourneyPathSortBy[]
    | null
    | undefined
) {
  const values = Array.isArray(sortBy)
    ? sortBy
    : [sortBy ?? DEFAULT_CUSTOMER_JOURNEY_PATH_SORT_BY];
  const uniqueValues = Array.from(new Set(values));

  return uniqueValues.length > 0
    ? uniqueValues
    : [DEFAULT_CUSTOMER_JOURNEY_PATH_SORT_BY];
}

export function buildCustomerJourneyOverviewSearchParams(
  scope: CustomerJourneyScope,
  input: CustomerJourneyDateFetchInput
) {
  return buildCustomerJourneyBaseSearchParams(scope, input);
}

export function buildCustomerJourneyRoleBreakdownSearchParams(
  scope: CustomerJourneyScope,
  input: CustomerJourneyRoleBreakdownFetchInput
) {
  const searchParams = buildCustomerJourneyBaseSearchParams(scope, input);
  appendCustomerTarget(searchParams, input.customerType ?? scope.customerType, {
    includeTotal: false,
  });
  return searchParams;
}

export function buildCustomerJourneyPathsSearchParams(
  scope: CustomerJourneyScope,
  input: CustomerJourneyPathsFetchInput
) {
  const searchParams = buildCustomerJourneyBaseSearchParams(scope, input);
  const breakdownDimension =
    input.breakdownDimension ??
    DEFAULT_CUSTOMER_JOURNEY_PATH_BREAKDOWN_DIMENSION;
  const limit = normalizePathLimit(input.limit);
  const offset = normalizeOffset(input.offset);
  const sortBy = normalizeSortBy(input.sortBy);
  const sortOrder = input.sortOrder ?? DEFAULT_CUSTOMER_JOURNEY_PATH_SORT_ORDER;
  const filters = normalizeFilters(input.filters ?? scope.filters);

  searchParams.append("breakdown_dimension", breakdownDimension);
  searchParams.append("limit", limit.toString());
  for (const metric of sortBy) {
    searchParams.append("sort_by", metric);
  }
  searchParams.append("descending", `${sortOrder === "desc"}`);
  appendCustomerTarget(searchParams, input.customerType ?? scope.customerType, {
    includeTotal: true,
  });

  if (offset !== null) {
    searchParams.append("offset", offset.toString());
  }

  if (filters) {
    searchParams.append("filters", filters);
  }

  return searchParams;
}

export function buildCustomerJourneyOrderTouchpointsSearchParams(
  input: CustomerJourneyOrderTouchpointsFetchInput
) {
  const searchParams = new URLSearchParams();
  searchParams.append("order_id", input.orderId);
  searchParams.append(
    "limit",
    normalizeOrderTouchpointsLimit(input.limit).toString()
  );
  searchParams.append(
    "offset",
    (normalizeOffset(input.offset) ?? 0).toString()
  );
  return searchParams;
}

export async function fetchCustomerJourneyOverviewWithScope({
  baseUrl,
  scope,
  input,
  token,
  timeoutMs,
  maxRetries,
  retryDelayMs,
  requestId,
  apiBasePath,
}: FetchCustomerJourneyOverviewOptions): Promise<CustomerJourneyOverviewResponse> {
  return getTracifyApiJson<CustomerJourneyOverviewResponse>({
    baseUrl,
    path: "/analytics/customer_journey/overview/",
    searchParams: buildCustomerJourneyOverviewSearchParams(scope, input),
    token,
    resource: "customer journey overview",
    timeoutMs,
    maxRetries,
    retryDelayMs,
    requestId,
    apiBasePath,
  });
}

export async function fetchCustomerJourneyOverviewWithContext({
  context,
  input,
  ...options
}: CustomerJourneyFetchWithContextOptions<CustomerJourneyDateFetchInput>): Promise<CustomerJourneyOverviewResponse> {
  return fetchCustomerJourneyOverviewWithScope({
    ...options,
    scope: createCustomerJourneyScopeFromTracifyContext(context, input),
    input,
  });
}

export async function fetchCustomerJourneyRoleBreakdownWithScope({
  baseUrl,
  scope,
  input,
  token,
  timeoutMs,
  maxRetries,
  retryDelayMs,
  requestId,
  apiBasePath,
}: FetchCustomerJourneyRoleBreakdownOptions): Promise<CustomerJourneyRoleBreakdownResponse> {
  return getTracifyApiJson<CustomerJourneyRoleBreakdownResponse>({
    baseUrl,
    path: "/analytics/customer_journey/initiator_holder_closer/",
    searchParams: buildCustomerJourneyRoleBreakdownSearchParams(scope, input),
    token,
    resource: "customer journey role breakdown",
    timeoutMs,
    maxRetries,
    retryDelayMs,
    requestId,
    apiBasePath,
  });
}

export async function fetchCustomerJourneyRoleBreakdownWithContext({
  context,
  input,
  ...options
}: CustomerJourneyFetchWithContextOptions<CustomerJourneyRoleBreakdownFetchInput>): Promise<CustomerJourneyRoleBreakdownResponse> {
  return fetchCustomerJourneyRoleBreakdownWithScope({
    ...options,
    scope: createCustomerJourneyScopeFromTracifyContext(context, input),
    input,
  });
}

export async function fetchCustomerJourneyPathsWithScope({
  baseUrl,
  scope,
  input,
  token,
  timeoutMs,
  maxRetries,
  retryDelayMs,
  requestId,
  apiBasePath,
}: FetchCustomerJourneyPathsOptions): Promise<CustomerJourneyBreakdownResponse> {
  return getTracifyApiJson<CustomerJourneyBreakdownResponse>({
    baseUrl,
    path: "/analytics/customer_journey/conversion_paths/",
    searchParams: buildCustomerJourneyPathsSearchParams(scope, input),
    token,
    resource: "customer journey conversion paths",
    timeoutMs,
    maxRetries,
    retryDelayMs,
    requestId,
    apiBasePath,
  });
}

export async function fetchCustomerJourneyPathsWithContext({
  context,
  input,
  ...options
}: CustomerJourneyFetchWithContextOptions<CustomerJourneyPathsFetchInput>): Promise<CustomerJourneyBreakdownResponse> {
  return fetchCustomerJourneyPathsWithScope({
    ...options,
    scope: createCustomerJourneyScopeFromTracifyContext(context, input),
    input,
  });
}

export async function fetchCustomerJourneyOrderTouchpoints({
  baseUrl,
  input,
  token,
  timeoutMs,
  maxRetries,
  retryDelayMs,
  requestId,
  apiBasePath,
}: FetchCustomerJourneyOrderTouchpointsOptions): Promise<CustomerJourneyOrderTouchpointsResponse> {
  return getTracifyApiJson<CustomerJourneyOrderTouchpointsResponse>({
    baseUrl,
    path: `/analytics/customer_journey/${encodeURIComponent(
      input.siteId
    )}/order_touchpoints`,
    searchParams: buildCustomerJourneyOrderTouchpointsSearchParams(input),
    token,
    resource: "customer journey order touchpoints",
    timeoutMs,
    maxRetries,
    retryDelayMs,
    requestId,
    apiBasePath,
  });
}
